import { CSSProperties, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Column } from 'react-table';
import styled from 'styled-components';
import { capitalize } from 'utilities';
import { Text } from 'shared/Typhography';
import { Roles } from 'shared/enums';

import { MODAL_TYPES, useGlobalModalContext } from '../../../../../context/GlobalModal';
import { Button, TableComponent } from '../../../../../shared/Components';
import { ActionButtons } from '../../../../../shared/Components/Table/Table';
import { strings } from '../../../../../strings';
import { Collection } from '../../../../../types';
import DeleteCollectionButton from '../../CreateCollectionScreen/DeleteCollectionButton';

const RoleCell = styled.span`
  padding-left: 4px;
  font-weight: bold;
  color: ${({ theme }) => theme.colors.black};
`;

const TextCell = styled(Text)<Pick<CSSProperties, 'width'>>`
  cursor: pointer;
  padding-left: 4px;
  width: ${({ width }) => width};
`;

interface Data {
  firebaseId: string;
  name: string;
  keywords: string;
  puzzles: number;
  accessibleToRoles: Array<string>;
  isAdminOnly: boolean;
  action: () => void;
}

interface CollectionTableProps {
  data: any;
}

export const CollectionTable = ({ data }: CollectionTableProps) => {
  const { showModal } = useGlobalModalContext();
  const history = useHistory();

  const openEditModal = (collection: Collection) => {
    showModal(MODAL_TYPES.EDIT_COLLECTION_MODAL, {
      title: strings.modals.editCategory.title,
      collection: collection,
    });
  };
  const headers: Column<Data>[] = useMemo(
    () => [
      {
        Header: strings.collectionTableHeaderName,
        accessor: 'name',
        Cell: (cell) => (
          <TextCell
            width={`${(window.screen.width * 22) / 100}px`}
            ellipsisOnRow={1}
            onClick={() => history.push(`/puzzles?category=${cell.row.original.firebaseId}`)}
          >
            {cell.row.original.name}
          </TextCell>
        ),
        width: '22%',
      },
      {
        Header: strings.collectionTableHeaderType,
        accessor: 'accessibleToRoles',
        Cell: (cell) => {
          const role = cell.row.original.isAdminOnly ? Roles.Admin : Roles.None;

          return <RoleCell>{capitalize(role)}</RoleCell>;
        },
        width: '10%',
        sortType: (rowA) => (rowA.original.isAdminOnly ? 1 : -1),
      },
      {
        Header: strings.collectionTableHeaderKeywords,
        accessor: 'keywords',
      },
      {
        Header: strings.collectionTableHeaderNumberOfPuzzles,
        accessor: 'puzzles',
        Cell: (cell) => (
          <span
            onClick={() => history.push(`/puzzles?category=${cell.row.original.firebaseId}`)}
            style={{ cursor: 'pointer' }}
          >
            {cell.row.original.puzzles}
          </span>
        ),
      },
      {
        Header: strings.collectionTableHeaderAction,
        accessor: 'action',
        Cell: (cell) => (
          <ActionButtons>
            <Button
              variant="secondary"
              onClick={() => openEditModal(cell.row.original)}
              width={100}
              height={30}
            >
              Edit
            </Button>

            <DeleteCollectionButton
              name={cell.row.original.name}
              collectionId={cell.row.original.firebaseId}
            />
          </ActionButtons>
        ),
      },
    ],
    [],
  );

  return <TableComponent columns={headers} data={data} />;
};
